import React, { useState, useEffect, useRef } from 'react';
import { FileText, RefreshCw } from 'lucide-react';
import { AIAgentScenario, getAgentScenarios } from '../../lib/aiAgents';
import AgentScenarioCard from './AgentScenarioCard';

interface AgentScenariosPanelProps {
  agentId: string;
  refreshTrigger: number;
  onEditScenario: (scenarioId: string) => void;
}

export default function AgentScenariosPanel({ agentId, refreshTrigger, onEditScenario }: AgentScenariosPanelProps) {
  const [scenarios, setScenarios] = useState<AIAgentScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [newScenarioIds, setNewScenarioIds] = useState<Set<string>>(new Set());
  const prevIdsRef = useRef<Set<string>>(new Set());

  const loadScenarios = async () => {
    try {
      const data = await getAgentScenarios(agentId);

      const currentIds = new Set(data.map(s => s.id));
      const prevIds = prevIdsRef.current;
      const freshIds = new Set<string>();
      currentIds.forEach(id => {
        if (!prevIds.has(id)) freshIds.add(id);
      });
      prevIdsRef.current = currentIds;

      if (freshIds.size > 0) {
        setNewScenarioIds(freshIds);
        setTimeout(() => setNewScenarioIds(new Set()), 3000);
      }

      setScenarios(data);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScenarios();
  }, [agentId, refreshTrigger]);

  const activeCount = scenarios.filter(s => s.is_active).length;

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Scenarios</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
            {activeCount}
          </span>
        </div>
        <button
          onClick={loadScenarios}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh scenarios"
        >
          <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : scenarios.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No scenarios yet</p>
            <p className="text-xs text-gray-400 mt-1">Add a scenario from the chat to create real-world examples</p>
          </div>
        ) : (
          scenarios.map(scenario => (
            <AgentScenarioCard
              key={scenario.id}
              scenario={scenario}
              isNew={newScenarioIds.has(scenario.id)}
              onDeleted={loadScenarios}
              onUpdated={loadScenarios}
              onEdit={onEditScenario}
            />
          ))
        )}
      </div>
    </div>
  );
}
